import { useMemo, useState } from 'react';
import { useAuth, useAuthActions } from '../state/AuthContext';
import { useAppState, useDispatch } from '../state/AppContext';
import { ADD_SEED_ARTIST } from '../state/actions';
import AddFavoriteModal from './AddFavoriteModal';
import '../styles/favorites.css';
import '../styles/friends.css';

function FavoritesSection({ filterQuery = '' }) {
  const { user, favorites } = useAuth();
  const { toggleFavorite, showAuthModal } = useAuthActions();
  const { seedArtists } = useAppState();
  const dispatch = useDispatch();
  const [showAddModal, setShowAddModal] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);

  const filteredFavorites = useMemo(() => {
    if (!filterQuery.trim()) return favorites;
    const q = filterQuery.toLowerCase();
    return favorites.filter((fav) => fav.artistName.toLowerCase().includes(q));
  }, [favorites, filterQuery]);

  const selectedNames = useMemo(
    () => new Set(seedArtists.map((a) => a.name)),
    [seedArtists]
  );

  const handleAddSeed = (fav) => {
    dispatch({
      type: ADD_SEED_ARTIST,
      payload: {
        id: fav.artistId || `fav-${fav.artistName}`,
        name: fav.artistName,
        genres: [],
        image: fav.artistImage,
      },
    });
  };

  return (
    <div className="favorites-section">
      {user && (
        <div className="favorites-action-bar">
          <button
            className="favorites-add-btn"
            onClick={() => setShowAddModal(true)}
            title="Add favorites"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span className="add-btn-label">Add Favorite</span>
          </button>
        </div>
      )}

      {/* Signed out — placeholder */}
      {!user && (
        <div className="favorites-scroll">
          <button
            className="section-placeholder-card"
            onClick={() => showAuthModal('register')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="20" height="20">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span className="section-placeholder-text">Sign in to save favorites</span>
          </button>
        </div>
      )}

      {/* Signed in, no favorites — empty placeholder */}
      {user && favorites.length === 0 && (
        <div className="favorites-scroll">
          <div className="section-placeholder-card empty">
            <span className="section-placeholder-text">Saved favorites will appear here</span>
          </div>
        </div>
      )}

      {/* Signed in, has favorites */}
      {user && favorites.length > 0 && (
        <div className="favorites-scroll">
          {filteredFavorites.map((fav) => (
            <div
              key={fav.artistName}
              className={`favorite-card-row ${selectedNames.has(fav.artistName) ? 'selected' : ''}`}
              onClick={() => handleAddSeed(fav)}
              role="button"
              tabIndex={0}
              title={`Add ${fav.artistName} as seed`}
            >
              {fav.artistImage ? (
                <img className="favorite-card-image" src={fav.artistImage} alt={fav.artistName} />
              ) : (
                <div className="favorite-card-image favorite-card-placeholder">
                  {fav.artistName.charAt(0)}
                </div>
              )}
              <span className="favorite-card-name">{fav.artistName}</span>
              <button
                className="favorite-card-remove-inline"
                onClick={(e) => { e.stopPropagation(); setConfirmRemove(fav); }}
                title="Remove favorite"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
      {showAddModal && <AddFavoriteModal onClose={() => setShowAddModal(false)} />}
      {confirmRemove && (
        <div className="auth-modal-overlay" onClick={(e) => e.target === e.currentTarget && setConfirmRemove(null)}>
          <div className="confirm-remove-modal">
            <p className="confirm-remove-text">
              Remove <strong>{confirmRemove.artistName}</strong> from favorites?
            </p>
            <div className="confirm-remove-actions">
              <button className="confirm-remove-cancel" onClick={() => setConfirmRemove(null)}>
                Cancel
              </button>
              <button className="confirm-remove-confirm" onClick={() => {
                toggleFavorite(confirmRemove.artistName, confirmRemove.artistId, confirmRemove.artistImage);
                setConfirmRemove(null);
              }}>
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FavoritesSection;
